<template>
  <div class="main">
    <!-- title -->
    <h3 class="title" content="edit" placement="bottom">
      Admin User Management
      <el-button style="width: 180px" type="primary" @click="handleAdd()"
        >Create</el-button
      >
      <span class="search-toggle" @click="searchExpanded = !searchExpanded">
        {{ searchExpanded ? "Hide Filters" : "Show Filters" }}
        <em class="el-icon-arrow-up" :class="{ 'is-collapsed': !searchExpanded }"></em>
      </span>
    </h3>
    <!-- search -->
    <div class="searchBox" v-show="searchExpanded">
      <el-form :model="formInline" ref="formInline" class="demo-form-inline">
        <div class="search-grid">
          <el-form-item prop="lastName">
            <el-input
              v-model="formInline.lastName"
              placeholder="Last Name"
              @keyup.enter.native="submitSearch"
              maxlength="50"
            ></el-input>
          </el-form-item>
          <el-form-item prop="firstName">
            <el-input
              v-model="formInline.firstName"
              placeholder="First Name"
              @keyup.enter.native="submitSearch"
              maxlength="50"
            ></el-input>
          </el-form-item>
        </div>
      </el-form>
      <div class="search-toolbar">
        <div class="search-toolbar__actions">
          <el-button type="primary" class="search" @click="submitSearch"
            >Search</el-button
          >
          <el-button class="reset" @click="resetForm('formInline')"
            >Reset</el-button
          >
        </div>
      </div>
    </div>
    <!-- toolbar, table & pagination share one panel -->
    <div class="client-panel">
      <div class="client-panel__toolbar">
        <div class="search-toolbar__extra">
          <columns-control
            :columns="columnsConfig"
            storage-key="cms.adminUserManagement.columns"
            @change="onColumnsChange"
          ></columns-control>
          <!-- filter & sort by -->
          <el-dropdown trigger="click" ref="filter">
          <span class="el-dropdown-link sortbox">
            <em class="filter"></em>
            Filter
          </span>
          <el-dropdown-menu slot="dropdown">
            <el-form
              ref="formFilter"
              :model="formFilter"
              style="width: 300px; padding: 14px 24px"
            >
              <el-form-item
                label="Sales Office"
                style="margin-bottom: 12px"
                prop="salesOffice"
              >
                <el-select
                  v-model="formFilter.salesOffice"
                  :popper-append-to-body="false"
                  @change="changeSalesOffice"
                >
                  <el-option value="All">All</el-option>
                  <el-option
                    v-for="(item, index) in salesOfficeList"
                    :key="index"
                    :label="item.name"
                    :value="item.code"
                  ></el-option>
                </el-select>
              </el-form-item>
              <el-form-item
                label="Region"
                style="margin-bottom: 12px"
                prop="region"
              >
                <el-select
                  v-model="formFilter.region"
                  :popper-append-to-body="false"
                >
                  <el-option value="All">All</el-option>
                  <el-option
                    v-for="(item, index) in regionList"
                    :key="index"
                    :label="item.name"
                    :value="item.code"
                  ></el-option>
                </el-select>
              </el-form-item>
              <div class="filterButton">
                <el-form-item style="margin-right: 15px; margin-bottom: 0">
                  <el-button
                    class="reset"
                    @click="
                      $refs.filter.hide();
                      resetForm('formFilter');
                    "
                    >Reset</el-button
                  >
                </el-form-item>
                <el-form-item style="margin-bottom: 0">
                  <el-button
                    type="primary"
                    class="search"
                    @click="
                      () => {
                        $refs.filter.hide();
                        submitSearch();
                      }
                    "
                    >Apply</el-button
                  >
                </el-form-item>
              </div>
            </el-form>
          </el-dropdown-menu>
        </el-dropdown>
        <el-dropdown trigger="click" @command="handleCommand">
          <span class="el-dropdown-link sortbox">
            <em class="sort"></em>
            Sort by
          </span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="userId,desc"
              >Admin ID (descending order)</el-dropdown-item
            >
            <el-dropdown-item command="userId,asc"
              >Admin ID (ascending order)</el-dropdown-item
            >
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </div>
    <!-- table -->
    <el-table :data="tableData">
      <el-table-column v-if="isColumnVisible('userId')" prop="userId" label="ID" width="100"> </el-table-column>
      <el-table-column v-if="isColumnVisible('userLevel')" label="User Permission" width="150">
        <template slot-scope="scope">{{
          $commonjs.cmsUserLevelMap(scope.row.userLevel)
        }}</template>
      </el-table-column>
      <el-table-column v-if="isColumnVisible('salesOffices')" label="Sales Office" width="120">
        <template slot-scope="scope">
          {{ scope.row.salesOffices && scope.row.salesOffices.join(", ") }}
        </template>
      </el-table-column>
      <el-table-column v-if="isColumnVisible('regions')" label="Region">
        <template slot-scope="scope">
          {{ scope.row.regions && scope.row.regions.join(", ") }}
        </template>
      </el-table-column>
      <el-table-column v-if="isColumnVisible('fullName')" label="Full Name">
        <template slot-scope="scope">{{
          scope.row.lastName + ", " + scope.row.firstName
        }}</template>
      </el-table-column>
      <el-table-column
        v-if="isColumnVisible('lastLoginAt')"
        prop="lastLoginAt"
        label="Last login"
        :formatter="$commonjs.tableFormatDateTime"
      >
      </el-table-column>
      <el-table-column label="Operation" width="120">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" content="Edit" placement="top">
            <em
              class="edit"
              style="margin-right: 10px"
              @click="handleEdit(scope.row)"
            ></em>
          </el-tooltip>
          <el-tooltip
            v-if="showSave"
            class="item"
            effect="dark"
            content="More"
            placement="top"
          >
            <el-dropdown
              trigger="click"
              @command="(command) => handleMore(command, scope.row)"
            >
              <span class="el-dropdown-link sortbox">
                <em class="more"></em>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="deleteAccount"
                  >Delete account</el-dropdown-item
                >
                <el-dropdown-item command="restPassword"
                  >Reset password</el-dropdown-item
                >
              </el-dropdown-menu>
            </el-dropdown>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>
    <!-- page -->
    <app-pagination
      @size-change="
        (v) => {
          currentSize = v;
          currentPage = 1;
          getList();
        }
      "
      @current-change="
        (v) => {
          currentPage = v;
          getList();
        }
      "
      :current-page="currentPage"
      :page-sizes="[5, 10, 20, 50]"
      :total="total"
      :page-size="currentSize"
    ></app-pagination>
    </div>
  </div>
</template>

<script>
import {
  cmsUserList,
  cmsUserDelete,
  cmsUserResetPwd,
  getSalesOffice,
  getSalesRegion,
} from "@/request/api";
export default {
  name: "cmsUser",
  data() {
    return {
      showSave: true,
      formInline: {
        lastName: "",
        firstName: "",
      },
      tableData: [],
      currentSize: 10,
      currentPage: 1,
      sort: "userId,desc",
      total: 0,
      dialogUserVisible: false,
      formLabelWidth: "96px",
      formUser: {
        id: 0,
        username: "",
        password: "",
        roles: [],
      },
      formFilter: {
        salesOffice: "All",
        region: "All",
      },
      salesOfficeList: [],
      regionList: [],
      searchExpanded: true,
      columnsConfig: [
        { prop: "userId", label: "ID", visible: true },
        { prop: "userLevel", label: "User Permission", visible: true },
        { prop: "salesOffices", label: "Sales Office", visible: true },
        { prop: "regions", label: "Region", visible: true },
        { prop: "fullName", label: "Full Name", visible: true },
        { prop: "lastLoginAt", label: "Last login", visible: true },
      ],
      visibleColumnProps: [
        "userId",
        "userLevel",
        "salesOffices",
        "regions",
        "fullName",
        "lastLoginAt",
      ],
    };
  },
  methods: {
    isColumnVisible(prop) {
      return this.visibleColumnProps.includes(prop);
    },
    onColumnsChange(visibleProps) {
      this.visibleColumnProps = visibleProps;
    },
    showBtn(val) {
      console.log(this.$store.state.permissions);
      var permissions = this.$store.state.permissions.filter(
        (item) => item == val
      );
      if (!permissions[0]) {
        this.showSave = false;
      }
    },
    getFilterConfig() {
      getSalesOffice().then((res) => {
        this.salesOfficeList = res.data.payload;
      });
    },
    changeSalesOffice(v) {
      if (v == "All") {
        this.clearRegion();
      } else {
        this.clearRegion();
        getSalesRegion(v).then((res) => {
          this.regionList = res.data.payload;
        });
      }
    },
    clearRegion() {
      this.regionList = [];
      this.$set(this.formFilter, "region", "");
    },
    handleCommand(command) {
      this.sort = command;
      this.submitSearch();
    },
    getList() {
      let params = {
        page: this.currentPage - 1,
        size: this.currentSize,
        sort: this.sort,
      };
      if (this.formInline.firstName)
        params["firstName"] = this.formInline.firstName;
      if (this.formInline.lastName)
        params["lastName"] = this.formInline.lastName;
      if (this.formFilter.salesOffice && this.formFilter.salesOffice != "All")
        params["salesOffice"] = this.formFilter.salesOffice;
      if (this.formFilter.region && this.formFilter.region != "All")
        params["region"] = this.formFilter.region;
      cmsUserList(params).then((res) => {
        this.tableData = res.data.content;
        this.total = res.data.totalElements;
      });
    },
    handleAdd() {
      this.$router.push({ path: "/adminCreateUser" });
    },
    handleEdit(row) {
      this.$router.push({ path: "/adminEditUser/" + row.id });
    },
    submitSearch() {
      this.currentPage = 1;
      this.getList();
    },
    resetForm(formName) {
      this.$refs[formName].resetFields();
      this.getList();
    },
    handleMore(command, row) {
      if (command == "deleteAccount") {
        const h = this.$createElement;
        const msgwarn = "Are you sure to delete this record?";
        this.$msgbox({
          message: h("div", { style: "padding:15px 0" }, [
            h(
              "p",
              {
                style:
                  "color:#003448;text-align:center;font-size:20px;font-weight:bold;line-height: 1.5;",
              },
              msgwarn
            ),
            h(
              "p",
              {
                style:
                  "color:#003448;text-align:center;font-size:20px;font-weight:bold;line-height: 1.5;",
              },
              row.firstName + ", " + row.lastName
            ),
          ]),
          showCancelButton: true,
          confirmButtonText: "Confirm",
          cancelButtonText: "Cancel",
          confirmButtonClass: "msg_button_half",
          cancelButtonClass: "msg_button_half",
          closeOnPressEscape: false,
          closeOnClickModal: false,
          callback: (action, instance) => {
            let button = action;
            if (button == "confirm") {
              cmsUserDelete(row.id).then((res) => {
                if (res.data.status == "00") {
                  this.$message.success("Admin user is deleted");
                  this.getList();
                } else {
                  this.$message.error(res.data.msg);
                }
              });
            }
          },
        });
      } else if (command == "restPassword") {
        const h = this.$createElement;
        this.$prompt(
          h("div", { style: "padding:0 0 15px" }, [
            h(
              "p",
              {
                style:
                  "color:#003448;text-align:center;font-size:20px;margin-bottom:20px;font-weight:bold;line-height: 1.5;",
              },
              `Create a temporary password`
            ),
            h(
              "p",
              { style: "color:#003448;font-size:13px;margin-bottom:-20px;" },
              "Temporary Password"
            ),
          ]),
          "",
          {
            showClose: false,
            showCancelButton: true,
            confirmButtonText: "Confirm",
            cancelButtonText: "Cancel",
            showInput: true,
            inputPlaceholder: "Please enter temporary password",
            confirmButtonClass: "msg_button",
            cancelButtonClass: "msg_button",
            closeOnPressEscape: false,
            closeOnClickModal: false,
            inputValue: this.$commonjs.randomPassword(12),
            inputValidator: (v) => {
              let reg = new RegExp("[\\u4E00-\\u9FFF]+", "g");
              if (!v) {
                return "Password can not be empty";
              }
              if (v.indexOf(" ") != -1) {
                return "Password can not include space";
              }
              // if(v.length>20||v.length<8){
              //   return "Password's length must be 8-20"
              // }
              if (reg.test(v)) {
                return "Password can not contain Chinese";
              } else {
                return true;
              }
            },
          }
        )
          .then(({ value }) => {
            let params = { newPwd: this.$encryptedRSAOAEP(value) };
            cmsUserResetPwd(row.id, params).then((res) => {
              if (res.data.status == "00") {
                this.$message.success(`The temporary password is ${value}. Email with temporary nassword has been sent to user.`);
                this.getList();
              } else {
                this.$message.error(res.data.msg);
              }
            });
          })
          .catch(() => {});
      }
    },
  },
  created: function () {
    this.$emit("header", true);
    this.getList();
    this.getFilterConfig();
    this.showBtn("CONTENT_WRITE");
  },
  beforeRouteLeave(to, from, next) {
    if (to.name == "cmsEditUser") {
      from.meta.keepAlive = true;
    } else {
      from.meta.keepAlive = false;
    }
    next();
  },
  activated() {
    if (this.$route.params.update) {
      this.getList();
    }
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.search-toggle {
  display: inline-flex;
  align-items: center;
  column-gap: 4px;
  float: right;
  margin-right: 20px;
  line-height: 40px;
  font-size: 14px;
  font-weight: 600;
  color: #003448;
  cursor: pointer;
}
.search-toggle .el-icon-arrow-up {
  transition: transform 0.2s ease;
}
.search-toggle .el-icon-arrow-up.is-collapsed {
  transform: rotate(180deg);
}

.searchBox {
  padding: 20px;
  margin-bottom: 20px;
  background: #fff;
  border: 1px solid #e6ebee;
  border-radius: 8px;
}

.search-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  column-gap: 20px;
}

.search-grid /deep/ .el-form-item {
  margin-bottom: 16px;
}

.search-grid /deep/ .el-input__inner {
  width: 100%;
}

.search-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  row-gap: 12px;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid #e6ebee;
}

.search-toolbar__actions .el-button {
  min-width: 96px;
}

.search-toolbar__extra {
  display: flex;
  align-items: center;
  column-gap: 20px;
}

/* Toolbar, table & pagination merged into one panel instead of three separate cards. */
.client-panel {
  background: #fff;
  border: 1px solid #e6ebee;
  border-radius: 8px;
  overflow: hidden;
}

.client-panel__toolbar {
  display: flex;
  justify-content: flex-end;
  padding: 14px 20px;
  border-bottom: 1px solid #e6ebee;
}

.client-panel__tags {
  display: flex;
  padding: 12px 20px 0;
}

.client-panel /deep/ .el-table {
  border: none;
  border-radius: 0;
}

.client-panel /deep/ .app-pagination {
  margin-top: 0;
  border: none;
  border-radius: 0;
  border-top: 1px solid #e6ebee;
}

.filterButton {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
.sortbox {
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/cmsUser/User.vue